import {
  type ProductizedServicePriceInterval,
  type ProductizedServicePriceType,
} from '#app/__generated__/api.js';
import { type ExcludeRelayFutureAddedValue } from '#app/types/utils.js';
import { formatCurrency } from '#app/utilities/money.js';
import { INTERVAL_LABELS, PRICE_TYPE_LABELS } from '../constants.js';

const getIntervalLabel = (
  interval: ExcludeRelayFutureAddedValue<ProductizedServicePriceInterval>,
): string => INTERVAL_LABELS[interval];

const getPriceTypeLabel = (
  type: ExcludeRelayFutureAddedValue<ProductizedServicePriceType>,
): string => PRICE_TYPE_LABELS[type];

export const constructPricingLabel = (
  price: null | number,
  interval?: null | ProductizedServicePriceInterval,
): string => {
  /**
   * Relay lets us gracefully handle anytime the values
   * of an `enum` in GraphQL are expanded.
   *
   * We want know about a situation where the user has created a service
   * with an `enum` that the Client doesn't yet support.
   *
   * - @Boeing787
   */
  if (
    price === null ||
    price === undefined ||
    interval === '%future added value'
  ) {
    throw new Error(
      `Attempted to construct a productized service price label with price ${price} and interval ${interval}.`,
    );
  }

  if (price === 0) return 'Free';

  // formats the price and removes decimals/cents - this will need to be adjusted if we allow fractional pricing
  const formattedPrice = formatCurrency(price, {
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });

  if (!interval) return `${formattedPrice}`;

  return `${formattedPrice} / ${getIntervalLabel(interval)}`;
};

export const constructPricingTimelineLabel = (
  type: ProductizedServicePriceType,
  timeline?: null | string,
): string => {
  if (!type || type === '%future added value') {
    throw new Error(
      `Attempted to construct a productized service timeline label with type ${type} and timeline ${timeline}.`,
    );
  }

  return type && timeline
    ? `${getPriceTypeLabel(type)}, ${timeline}`
    : `${getPriceTypeLabel(type)}`;
};
